// JBF Admin API Service - Django Backend Integration
import { get, post, patch, del } from "@/services/axiosUitils";

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  user_type?: string;
  created_at?: string;
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
}

export interface Profile {
  id: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  avatar_url?: string;
  created_at?: string;
}

export interface Student {
  id: string;
  user_id: string;
  profile?: Profile;
  applications?: any[];
}

export interface Hostel {
  id: string;
  name: string;
  status?: string;
  address?: string;
  created_at?: string;
}

export interface Institution {
  id: string;
  name: string;
  type?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  total_students?: number | null;
  staff_count?: number | null;
  departments?: string[] | null;
  trustee_name?: string | null;
  trustee_contact?: string | null;
  status?: string | null;
  address?: string;
  created_at?: string;
}

export interface Donor {
  id: string;
  user_id: string;
  profile?: Profile;
  donations?: any[];
}

export interface Trustee {
  id: string;
  user_id: string;
  profile?: Profile;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: string;
}

// ============================================================================
// Authentication API
// ============================================================================

export const authAPI = {
  // Email/Password login
  login: async (email: string, password: string) => {
    return post<AuthResponse>("/auth/login/", {
      email,
      password,
    });
  },

  // Signup
  signup: async (data: {
    email: string;
    password: string;
    phone?: string;
    user_type?: string;
    first_name?: string;
    last_name?: string;
  }) => {
    return post<AuthResponse>("/auth/", data);
  },

  // Logout
  logout: async () => {
    return post("/auth/logout/");
  },

  // Refresh token
  refreshToken: async (refreshToken: string) => {
    return post<{ access: string }>("/auth/token-refresh/", {
      refresh: refreshToken,
    });
  },

  // Forgot password request
  forgotPasswordRequest: async (email: string) => {
    return post<{ message: string }>("/forgot-password-request/", {
      email,
    });
  },

  // Forgot password reset
  forgotPasswordReset: async (
    id: string,
    data: { new_password: string; confirm_password: string }
  ) => {
    return post(`/forgot-password-reset/${id}/`, data);
  },

  // Send OTP - Login uses /auth/login/, Register uses /auth/; both include role (selected user role)
  // Returns code_reference which must be used for verification
  sendOTP: async (
    phoneNumber: string,
    phoneCode: string = import.meta.env.VITE_PHONE_CODE as string,
    role?: string,
    forRegister?: boolean
  ) => {
    const payload: Record<string, string> = {
      phone_number: phoneNumber,
      phone_code: phoneCode,
    };
    if (role) {
      payload.role = role;
    }

    const endpoint = forRegister ? "/auth/" : "/auth/login/";
    return post<{ message: string; code_reference: string }>(endpoint, payload);
  },

  // Verify OTP - Login uses /auth/login/, Register uses /auth/; both include role (selected user role)
  // Returns auth tokens and user data
  verifyOTP: async (
    otp: string,
    codeReference: string,
    role?: string,
    forRegister?: boolean
  ) => {
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    const payload: Record<string, string> = {
      otp: otp.trim(),
      code_reference: codeReference.trim(),
    };
    if (role) {
      payload.role = role;
    }

    const endpoint = forRegister ? "/auth/" : "/auth/login/";
    return post<AuthResponse>(endpoint, payload);
  },

  // Email OTP - Send OTP (alternative method if backend supports it)
  sendEmailOTP: async (email: string) => {
    return post<{ message: string; code_reference?: string }>(
      "/auth/verify-email/send-otp/",
      {
        email,
      }
    );
  },

  // Email OTP - Verify OTP
  verifyEmailOTP: async (
    email: string,
    otp: string,
    codeReference?: string
  ) => {
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    return post<AuthResponse>("/auth/verify-email/verify-otp/", {
      email,
      otp,
      code_reference: codeReference.trim(),
    });
  },

  // Email OTP - Resend OTP
  resendEmailOTP: async (email: string, codeReference: string) => {
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    return post<{ message: string; code_reference?: string }>(
      "/auth/verify-email/resend-otp/",
      {
        email,
        code_reference: codeReference.trim(),
      }
    );
  },

  // General Resend OTP (for phone-based auth)
  resendOTP: async (phone: string, codeReference: string) => {
    if (!codeReference) {
      throw new Error("Code reference is required");
    }
    return post<{ message: string; code_reference?: string }>(
      "/auth/resend-otp/",
      {
        phone,
        code_reference: codeReference.trim(),
      }
    );
  },

  // Phone-based OTP (if backend supports it)
  // Note: These endpoints may need to be implemented in Django backend
  sendPhoneOTP: async (phone: string, portal?: string) => {
    return post<{ message: string }>("/auth/send-otp/", {
      phone,
      portal,
    });
  },

  verifyPhoneOTP: async (phone: string, otp: string, portal?: string) => {
    return post<AuthResponse>("/auth/verify-otp/", {
      phone,
      otp,
      portal,
    });
  },
};

// ============================================================================
// Profiles API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const profilesAPI = {
  get: async (userId: string) => {
    return get<Profile>(`/profiles/${userId}/`);
  },

  upsert: async (data: Partial<Profile>) => {
    return post<Profile>("/profiles/", data);
  },

  update: async (userId: string, data: Partial<Profile>) => {
    return patch<Profile>(`/profiles/${userId}/`, data);
  },
};

// ============================================================================
// User Roles API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const userRolesAPI = {
  getByUserId: async (userId: string) => {
    return get<UserRole[]>(`/user-roles/?user_id=${userId}`);
  },

  create: async (data: { user_id: string; role: string }) => {
    return post<UserRole>("/user-roles/", data);
  },
};

// ============================================================================
// Students API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const studentsAPI = {
  getAll: async () => {
    return get<Student[]>("/students/");
  },

  get: async (id: string) => {
    return get<Student>(`/students/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Student>(`/students/?user_id=${userId}`);
  },

  create: async (data: Partial<Student>) => {
    return post<Student>("/students/", data);
  },

  update: async (id: string, data: Partial<Student>) => {
    return patch<Student>(`/students/${id}/`, data);
  },

  getApplications: async (studentId: string) => {
    return get(`/students/${studentId}/applications/`);
  },
};

// ============================================================================
// Hostels API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const hostelsAPI = {
  getAll: async (params?: { status?: string }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params).toString()}`
      : "";
    return get<Hostel[]>(`/hostels/${queryParams}`);
  },

  get: async (id: string) => {
    return get<Hostel>(`/hostels/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Hostel[]>(`/hostels/?user_id=${userId}`);
  },

  create: async (data: Partial<Hostel>) => {
    return post<Hostel>("/hostels/", data);
  },

  update: async (id: string, data: Partial<Hostel>) => {
    return patch<Hostel>(`/hostels/${id}/`, data);
  },

  delete: async (id: string) => {
    return del(`/hostels/${id}/`);
  },

  approve: async (id: string) => {
    return post(`/hostels/${id}/approve/`);
  },

  reject: async (id: string, reason?: string) => {
    return post(`/hostels/${id}/reject/`, { reason });
  },
};

// ============================================================================
// Institutions API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const institutionsAPI = {
  getAll: async (params?: { status?: string }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params).toString()}`
      : "";
    return get<Institution[]>(`/jbf/institutions/${queryParams}`);
  },

  get: async (id: string) => {
    return get<Institution>(`/jbf/institutions/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Institution[]>(`/jbf/institutions/?user_id=${userId}`);
  },

  /** POST /jbf/institutions/setups — submit full onboarding payload */
  create: async (data: Partial<Institution>) => {
    return post<Institution>("/jbf/institutions/", data);
  },

  update: async (id: string, data: Partial<Institution>) => {
    return patch<Institution>(`/jbf/institutions/${id}/`, data);
  },

  delete: async (id: string) => {
    return del(`/jbf/institutions/${id}/`);
  },

  approve: async (id: string) => {
    return post(`/jbf/institutions/${id}/approve/`);
  },

  reject: async (id: string, reason?: string) => {
    return post(`/jbf/institutions/${id}/reject/`, { reason });
  },
};

// ============================================================================
// Donors API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const donorsAPI = {
  getAll: async () => {
    return get<Donor[]>("/donors/");
  },

  get: async (id: string) => {
    return get<Donor>(`/donors/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Donor>(`/donors/?user_id=${userId}`);
  },

  create: async (data: Partial<Donor>) => {
    return post<Donor>("/donors/", data);
  },

  getDonations: async (donorId: string) => {
    return get(`/donors/${donorId}/donations/`);
  },
};

// ============================================================================
// Trustees API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const trusteesAPI = {
  getAll: async () => {
    return get<Trustee[]>("/trustees/");
  },

  get: async (id: string) => {
    return get<Trustee>(`/trustees/${id}/`);
  },

  getByUserId: async (userId: string) => {
    return get<Trustee>(`/trustees/?user_id=${userId}`);
  },

  create: async (data: Partial<Trustee>) => {
    return post<Trustee>("/trustees/", data);
  },
};

// ============================================================================
// Applications API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const applicationsAPI = {
  getAll: async (params?: {
    student_id?: string;
    hostel_id?: string;
    status?: string;
  }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params as any).toString()}`
      : "";
    return get(`/applications/${queryParams}`);
  },

  get: async (id: string) => {
    return get(`/applications/${id}/`);
  },

  create: async (data: any) => {
    return post("/applications/", data);
  },

  update: async (id: string, data: any) => {
    return patch(`/applications/${id}/`, data);
  },
};

// ============================================================================
// Donations API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const donationsAPI = {
  getAll: async (params?: { donor_id?: string }) => {
    const queryParams = params
      ? `?${new URLSearchParams(params as any).toString()}`
      : "";
    return get(`/donations/${queryParams}`);
  },

  get: async (id: string) => {
    return get(`/donations/${id}/`);
  },

  create: async (data: any) => {
    return post("/donations/", data);
  },
};

// ============================================================================
// Scholarships API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const scholarshipsAPI = {
  getAll: async () => {
    return get("/scholarships/");
  },

  get: async (id: string) => {
    return get(`/scholarships/${id}/`);
  },

  create: async (data: any) => {
    return post("/scholarships/", data);
  },

  update: async (id: string, data: any) => {
    return patch(`/scholarships/${id}/`, data);
  },

  delete: async (id: string) => {
    return del(`/scholarships/${id}/`);
  },
};

// ============================================================================
// Statistics/Dashboard API
// ============================================================================
// Note: These endpoints may need to be implemented in Django backend
// Adjust paths based on your actual API structure

export const statsAPI = {
  getDashboardStats: async () => {
    return get("/stats/dashboard/");
  },

  getHostelStats: async (hostelId?: string) => {
    const query = hostelId ? `?hostel_id=${hostelId}` : "";
    return get(`/stats/hostels/${query}`);
  },

  getInstitutionStats: async (institutionId?: string) => {
    const query = institutionId ? `?institution_id=${institutionId}` : "";
    return get(`/stats/institutions/${query}`);
  },
};
